import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { MultiListContainer } from 'data/generated/graphql';

interface SelectedCollectionsContextValue {
  selectedCheckboxes: Partial<Record<string, boolean>>;
  setSelectedCheckboxes: React.Dispatch<React.SetStateAction<Partial<Record<string, boolean>>>>;
  allUnchecked: boolean;
  atLeastOneSelected: boolean;
  activeTabId: string;
  setActiveTabId: React.Dispatch<React.SetStateAction<string>>;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
  scrollPositionsRef: React.MutableRefObject<Partial<Record<string, number>>>;
  syncedCollectionsRef: React.MutableRefObject<Partial<Record<string, boolean>>>;
  erroredCollections: Partial<Record<string, boolean>>;
  setErroredCollection: (uuid: string, hasError: boolean) => void;
}

const SelectedCollectionsContext = createContext<SelectedCollectionsContextValue | null>(null);

export const SelectedCollectionsProvider = ({ children }: { children: React.ReactNode }) => {
  const { root } = useDataModelContext<MultiListContainer>();
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<Partial<Record<string, boolean>>>(() =>
    Object.fromEntries(root.collection.map((newswiresList) => [newswiresList.uuid, false]))
  );
  const [activeTabId, setActiveTabId] = useState('');
  const [erroredCollections, setErroredCollections] = useState<Partial<Record<string, boolean>>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollPositionsRef = useRef<Partial<Record<string, number>>>({});
  const syncedCollectionsRef = useRef<Partial<Record<string, boolean>>>({});

  const setErroredCollection = useCallback((uuid: string, hasError: boolean) => {
    setErroredCollections((prev) => ({ ...prev, [uuid]: hasError }));
  }, []);

  // Reset activeTabId if its collection is removed from root
  useEffect(() => {
    if (activeTabId && !root.collection.some((c) => c.uuid === activeTabId)) {
      setActiveTabId('');
    }
  }, [activeTabId, root.collection]);

  // Drop persisted scroll positions, synced flags, and error state for collections that have been deselected
  useEffect(() => {
    [scrollPositionsRef.current, syncedCollectionsRef.current].forEach((map) => {
      Object.keys(map).forEach((id) => {
        if (!selectedCheckboxes[id]) delete map[id]; // eslint-disable-line no-param-reassign
      });
    });
    setErroredCollections((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((id) => {
        if (!selectedCheckboxes[id]) delete next[id];
      });
      return next;
    });
  }, [selectedCheckboxes]);

  const allUnchecked = useMemo(
    () => Object.values(selectedCheckboxes).every((isChecked) => !isChecked),
    [selectedCheckboxes]
  );

  const atLeastOneSelected = useMemo(
    () => Object.values(selectedCheckboxes).some((isChecked) => isChecked),
    [selectedCheckboxes]
  );

  return (
    <SelectedCollectionsContext.Provider
      value={{
        selectedCheckboxes,
        setSelectedCheckboxes,
        allUnchecked,
        atLeastOneSelected,
        activeTabId,
        setActiveTabId,
        scrollContainerRef,
        scrollPositionsRef,
        syncedCollectionsRef,
        erroredCollections,
        setErroredCollection
      }}
    >
      {children}
    </SelectedCollectionsContext.Provider>
  );
};

export const useSelectedCollections = () => {
  const context = useContext(SelectedCollectionsContext);

  if (!context) {
    throw new Error('useSelectedCollections must be used within a SelectedCollectionsProvider');
  }

  return context;
};
